import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextExpenseReport } from './ExpenseReport';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ExpenseReportDA } from '../../../../services/admin/expense.report.service';
import { ExpenseDA } from '../../../../services/admin/expense.service';
import { Error } from '../../../../models/error.model';
import { Expense } from '../../../../models/expense.model';
import { ExpenseReport } from '../../../../models/expense.report.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { legendInvalid } from '../../../../utils/legend.data.util';
import { getError } from '../../../../languages/translations/response';
import { navBarShow } from '../../../../utils/nav.bar.util';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { normalizeStatus } from '../../../../libraries/status.document.library';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { foreignCurrencyPayload } from '../../../../components/element/InputForeignCurrency';
import { formatMoney, formatContact } from '../../../../scripts/format.value.script';
import { modalShow } from '../../../../utils/modal.util';
import ExpenseReportFormHeader from './ExpenseReportFormHeader';
import AppPreloader from '../../../../components/Preloader';
import AppPurchaseViewDraft from '../component/PurchaseViewDraft';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';
import ModalExpenseReportExpense, { MODAL_EXPENSE_REPORT_EXPENSE_ID, ExpenseReportExpenseData } from '../../../../components/modal/ModalExpenseReportExpense';
import AppModalAttachDocument from '../../../../components/modal/ModalAttachDocument';

export interface ExpenseReportUpdatePageProps {};

let errorResponse: Error, expenseReportResponse: ExpenseReport;

const MODAL_ATTACH_DOC_ID = 'modal-attach-doc-expense-report'

const ExpenseReportUpdatePage: React.FunctionComponent<ExpenseReportUpdatePageProps> = props => {
  const {asideMinimize, setAsideMinimize, setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, productsForCompany, servicesForCompany} = UseOutletContextExpenseReport()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [employee, setEmployee] = useState({value: '', valid: false, object: null as any})
  const [concerning, setConcerning] = useState({value: '', valid: false})
  const [expenseReport, setExpenseReport] = useState<ExpenseReport | null>(null)

  // Expense list state
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loadingExpenses, setLoadingExpenses] = useState(false)

  // Modal state
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [prefillVoucherFile, setPrefillVoucherFile] = useState<File | null>(null)
  const [prefillDocumentId, setPrefillDocumentId] = useState<string | null>(null)
  const [prefillDocumentName, setPrefillDocumentName] = useState<string | null>(null)

  const isEditable = expenseReport ? (expenseReport.status === 'draft' || expenseReport.status === 'rejected') : false

  const loadExpenseReport = async (id_company: string, id_expense_report: string) => {
    try {
      const response = await ExpenseReportDA.getExpenseReport(id_company, id_expense_report)
      if (response.status !== 200) {
        errorResponse = response.data
        GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
        return
      }

      expenseReportResponse = response.data
      expenseReportResponse.status = normalizeStatus(expenseReportResponse.status)

      setExpenseReport(expenseReportResponse)

      // Load expenses from report.lines
      const lines = expenseReportResponse.lines ?? []
      if (lines.length > 0) {
        setLoadingExpenses(true)
        const expenseResponses = await Promise.all(
          lines.map(line => ExpenseDA.getExpense(id_company, line.id_expense).catch(() => null))
        )
        const loadedExpenses: Expense[] = []
        for (let idx = 0; idx < lines.length; idx++) {
          const expRes = expenseResponses[idx]
          const expense = (expRes && expRes.status === 200) ? expRes.data : null
          if (expense) loadedExpenses.push(expense)
        }
        setExpenses(loadedExpenses)
        setLoadingExpenses(false)
      }

      // Header fields
      if (expenseReportResponse.id_employee) {
        setEmployee({value: expenseReportResponse.id_employee, valid: true, object: expenseReportResponse.employee ?? {contact_snapshot: expenseReportResponse.employee}})
      }
      if (expenseReportResponse.concerning) {
        setConcerning({value: expenseReportResponse.concerning, valid: true})
      }

      const reportEditable = expenseReportResponse.status === 'draft' || expenseReportResponse.status === 'rejected'

      if (reportEditable && companyForUser) {
        setAsideMinimize("on")
        navBarShow('button-navbar-aside-minimize')
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  // Add expense from modal data
  const addExpense = async (data: ExpenseReportExpenseData) => {
    if (!companyForUser || !expenseReport) return

    const idContact = expenseReport.id_contact
    if (!idContact) {
      GlobalSwal.fire({ title: getError('EMPLOYEE_NO_CONTACT', lang.code, 'Employee is not linked to a payroll contact'), icon: 'error', confirmButtonText: lang.labels.okGotIt })
      return
    }

    const companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}
    const providerObj = data.provider.valid ? data.provider.object : null
    const expenseConcerning = data.concerning.value || concerning.value

    try {
      const createRes = await ExpenseDA.postEmployeeExpense(
        companyForUser.id, idContact, expenseConcerning, data.date.value,
        companyForUser.currency, foreignCurrencyPayload(data.foreignCurrency),
        companyData, data.requirements.value, data.voucherFiles, providerObj
      )
      if (createRes.status !== 201 && createRes.status !== 200) {
        errorResponse = createRes.data
        GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
        return
      }

      const newExpense = createRes.data
      const lineRes = await ExpenseReportDA.addLine(companyForUser.id, expenseReport.id, newExpense.id)
      if (lineRes.status !== 201 && lineRes.status !== 200) {
        GlobalSwal.fire({ title: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
        return
      }

      // Reload the expense to get server-computed fields
      const reloadRes = await ExpenseDA.getExpense(companyForUser.id, newExpense.id).catch(() => null)
      const freshExpense = (reloadRes && reloadRes.status === 200) ? reloadRes.data : newExpense
      setExpenses(prev => [...prev, freshExpense])
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  // Update existing expense from modal data
  const updateExpense = async (index: number, data: ExpenseReportExpenseData) => {
    if (!companyForUser) return

    const expense = expenses[index]
    if (!expense) return

    const providerObj = data.provider.valid ? data.provider.object : null
    const expenseConcerning = data.concerning.value || concerning.value

    try {
      const updateRes = await ExpenseDA.putExpense(
        companyForUser.id, expense.id, providerObj, expenseConcerning, data.date.value,
        companyForUser.currency, foreignCurrencyPayload(data.foreignCurrency), null,
        data.requirements.value, data.voucherFiles
      )
      if (updateRes.status !== 200) {
        errorResponse = updateRes.data
        GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
        return
      }

      // Reload to get server state
      const reloadRes = await ExpenseDA.getExpense(companyForUser.id, expense.id).catch(() => null)
      const freshExpense = (reloadRes && reloadRes.status === 200) ? reloadRes.data : updateRes.data
      setExpenses(prev => prev.map((e, i) => i === index ? freshExpense : e))
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  // Remove expense (removeLine only — backend deletes the expense when its line is removed)
  const removeExpense = async (index: number) => {
    if (!companyForUser || !expenseReport) return

    const expense = expenses[index]
    if (!expense) return

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteThisExpense ?? lang.labels.areYouSure,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const removeRes = await ExpenseReportDA.removeLine(companyForUser.id, expenseReport.id, expense.id)
          if (removeRes.status === 200 || removeRes.status === 204) {
            setExpenses(prev => prev.filter((_, i) => i !== index))
          } else {
            errorResponse = removeRes.data
            GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
          }
        } catch (error) {
          errorWebHandler(lang, error)
        }
      }
    })
  }

  // Modal handlers — three add entries
  const openAddManual = () => {
    setEditingIndex(null)
    setEditingExpense(null)
    setPrefillVoucherFile(null)
    setPrefillDocumentId(null)
    setPrefillDocumentName(null)
    setTimeout(() => modalShow(MODAL_EXPENSE_REPORT_EXPENSE_ID), 50)
  }

  const openAddFromPdf = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/pdf'
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0]
      if (!file) return
      setEditingIndex(null)
      setEditingExpense(null)
      setPrefillVoucherFile(file)
      setPrefillDocumentId(null)
      setPrefillDocumentName(null)
      setTimeout(() => modalShow(MODAL_EXPENSE_REPORT_EXPENSE_ID), 50)
    }
    input.click()
  }

  const openAddFromExistingDoc = () => {
    setTimeout(() => modalShow(MODAL_ATTACH_DOC_ID), 50)
  }

  const handlePickDocument = (id_document_inbox: string, doc?: { name: string }) => {
    setEditingIndex(null)
    setEditingExpense(null)
    setPrefillVoucherFile(null)
    setPrefillDocumentId(id_document_inbox)
    setPrefillDocumentName(doc?.name ?? null)
    setTimeout(() => modalShow(MODAL_EXPENSE_REPORT_EXPENSE_ID), 100)
  }

  const openEditModal = (index: number) => {
    setEditingIndex(index)
    setEditingExpense(expenses[index])
    setPrefillVoucherFile(null)
    setPrefillDocumentId(null)
    setPrefillDocumentName(null)
    setTimeout(() => modalShow(MODAL_EXPENSE_REPORT_EXPENSE_ID), 50)
  }

  const handleModalSave = async (data: ExpenseReportExpenseData) => {
    if (editingIndex !== null) {
      await updateExpense(editingIndex, data)
    } else {
      await addExpense(data)
    }
    setEditingIndex(null)
    setEditingExpense(null)
    setPrefillVoucherFile(null)
    setPrefillDocumentId(null)
    setPrefillDocumentName(null)
  }

  const handleModalCancel = () => {
    setEditingIndex(null)
    setEditingExpense(null)
    setPrefillVoucherFile(null)
    setPrefillDocumentId(null)
    setPrefillDocumentName(null)
  }

  // Header-only update (concerning)
  const submitExpenseReportUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && expenseReport && employee.valid && concerning.valid) {
      try {
        await ExpenseReportDA.putExpenseReport(companyForUser.id, expenseReport.id, employee.value, concerning.value, expenseReport.created_date ?? '', companyForUser.currency, null, [])

        GlobalSwal.fire({
          title: lang.labels.successfullyUpdated,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then(() => {
          navigate(-1)
        })
      } catch (error) {
        errorWebHandler(lang, error)
      } finally {
        setLoadIndicator('off')
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!employee.valid) legendInvalid('legend-employee-required')
        if (!concerning.value) legendInvalid('legend-concerning-required')
        setLoadIndicator('off')
      })
    }
  }

  const submitExpenseReportDelete = async () => {
    if (companyForUser && expenseReport) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisExpenseReport,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ExpenseReportDA.deleteExpenseReport(companyForUser.id, expenseReport.id).then((response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then(() => {
                navigate('/main/purchase/expense-report/list', {replace: true})
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch((error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const computeExpenseTotal = (expense: Expense): number => {
    // Sum all contents across all requirements
    if (!expense.requirements?.length) return expense.amount?.total ?? 0
    let total = 0
    for (const req of expense.requirements) {
      for (const content of (req.contents ?? [])) {
        const price = Number(content.price_unit) || 0
        const qty = Number(content.quantity) || 1
        const lineAmount = price * qty
        if (content.tax_included) {
          total += lineAmount
        } else {
          const rate = content.tax_rate ?? 0
          total += lineAmount * (1 + rate)
        }
      }
    }
    return total || (expense.amount?.total ?? 0)
  }

  const getExpenseForeignAmount = (expense: Expense): { amount: number; code: string } | null => {
    if (!expense.foreign_currency?.code || !companyForUser) return null
    if (expense.foreign_currency.code === companyForUser.currency?.code) return null
    const total = computeExpenseTotal(expense)
    return { amount: total, code: expense.foreign_currency.code }
  }

  const computeGrandTotal = (): number => {
    // Use server amount if available, otherwise compute
    return expenses.reduce((sum, exp) => sum + (exp.amount?.total ?? computeExpenseTotal(exp)), 0)
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadExpenseReport(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.expenseReports, branch: lang.labels.editExpenseReport}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && productsForCompany && servicesForCompany && expenseReport
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="form">
              <ExpenseReportFormHeader mode="update" record={expenseReport as any} employee={employee} concerning={concerning} setEmployee={setEmployee} setConcerning={setConcerning}></ExpenseReportFormHeader>

              {/* Expense list — one line per expense */}
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.expenses}</h4>
                  </div>
                  { isEditable &&
                    <div className="card-toolbar">
                      <button className="btn btn-sm btn-light-primary me-2" type="button" onClick={openAddFromPdf}>
                        <span className="svg-icon svg-icon-5 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.uploadDocument}
                      </button>
                      <button className="btn btn-sm btn-light-primary me-2" type="button" onClick={openAddFromExistingDoc}>
                        <span className="svg-icon svg-icon-5 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM11.5 17.5C11.5 17.2 11.4 17 11.2 16.8C10.8 16.4 10.2 16.4 9.79999 16.8L8.09999 18.5C7.89999 18.7 7.89999 19.1 8.09999 19.3C8.29999 19.5 8.69999 19.5 8.89999 19.3L9.79999 18.4V21C9.79999 21.3 10 21.5 10.3 21.5C10.6 21.5 10.8 21.3 10.8 21V18.4L11.7 19.3C11.9 19.5 12.3 19.5 12.5 19.3C12.7 19.1 12.7 18.7 12.5 18.5L10.8 16.8C10.6 16.6 10.3 16.5 10 16.5C10 16.5 10 16.5 10 16.5Z" fill="black" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.attachExistingDocument}
                      </button>
                      <button className="btn btn-sm btn-light me-0" type="button" onClick={openAddManual}>
                        <span className="svg-icon svg-icon-5 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                            <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                            <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.manualEntry}
                      </button>
                    </div>
                  }
                </div>
                <div className="card-body pt-0">
                  { loadingExpenses
                    ?
                    <div className="text-center py-10">
                      <span className="spinner-border spinner-border-sm align-middle"></span>
                    </div>
                    : expenses.length === 0
                    ?
                    <div className="text-center text-muted py-10">
                      <span className="svg-icon svg-icon-3x svg-icon-muted mb-3 d-block">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                        </svg>
                      </span>
                      <div className="fs-6">{lang.labels.addExpense}</div>
                    </div>
                    :
                    <>
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
                          <thead>
                            <tr className="fw-bolder text-muted fs-7">
                              <th className="min-w-100px">{lang.labels.expenseNumber}</th>
                              <th className="min-w-120px">{lang.labels.merchant ?? lang.labels.provider}</th>
                              <th className="min-w-120px">{lang.labels.concerning}</th>
                              <th className="min-w-80px">{lang.labels.date}</th>
                              <th className="min-w-100px text-end">{lang.labels.amount}</th>
                              <th className="w-50px text-center">{lang.labels.receipt}</th>
                              { isEditable && <th className="w-80px text-end">{lang.labels.actions}</th> }
                            </tr>
                          </thead>
                          <tbody>
                            { expenses.map((expense, index) => {
                              const providerName = expense.provider ? formatContact(expense.provider) : '—'
                              const expTotal = expense.amount?.total ?? computeExpenseTotal(expense)
                              const foreignInfo = getExpenseForeignAmount(expense)
                              const currSymbol = companyForUser.currency?.symbol ?? companyForUser.currency?.code ?? ''

                              return (
                                <tr key={expense.id || index}>
                                  <td className="fw-bold">{expense.expense_number || `#${index + 1}`}</td>
                                  <td className="fw-bold">{providerName}</td>
                                  <td className="text-muted">{expense.concerning || '—'}</td>
                                  <td className="text-muted">{expense.created_date ? dateFormat(lang.code, expense.created_date) : '—'}</td>
                                  <td className="text-end">
                                    <span className="fw-bolder text-dark">{formatMoney(expTotal)} {currSymbol}</span>
                                    { foreignInfo &&
                                      <div className="text-muted fs-8">{formatMoney(foreignInfo.amount)} {foreignInfo.code}</div>
                                    }
                                  </td>
                                  <td className="text-center">
                                    { expense.vouchers && expense.vouchers.length > 0
                                      ? <ButtonVouchers vouchers={expense.vouchers}></ButtonVouchers>
                                      : <span className="text-muted">—</span>
                                    }
                                  </td>
                                  { isEditable &&
                                    <td className="text-end">
                                      <div className="d-flex justify-content-end gap-1">
                                        <button className="btn btn-icon btn-sm btn-light-primary" type="button" onClick={() => openEditModal(index)}>
                                          <span className="svg-icon svg-icon-5">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                              <path opacity="0.3" d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 1.99988 17.0825 1.99988C17.6224 1.99988 18.1402 2.21423 18.522 2.59595L21.4 5.47394C21.7817 5.85575 21.9962 6.37357 21.9962 6.91349C21.9962 7.45341 21.7817 7.97122 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75641 21.9115 2.989 21.9658C3.22158 22.0201 3.46497 22.0085 3.68699 21.932ZM5.54549 16.305L7.69449 18.455L12.052 16.871L11.558 16.377L7.62149 12.441L7.12749 11.947L5.54549 16.305Z" fill="black"/>
                                              <path d="M5.54549 16.305L13.486 4.755L19.242 10.511L7.69449 18.455L5.54549 16.305Z" fill="black"/>
                                            </svg>
                                          </span>
                                        </button>
                                        <button className="btn btn-icon btn-sm btn-light-danger" type="button" onClick={() => removeExpense(index)}>
                                          <span className="svg-icon svg-icon-5">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                              <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                              <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                              <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                            </svg>
                                          </span>
                                        </button>
                                      </div>
                                    </td>
                                  }
                                </tr>
                              )
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={isEditable ? 4 : 3}></td>
                              <td className="text-end">
                                <span className="fw-bolder fs-4 text-dark">{formatMoney(computeGrandTotal())} {companyForUser.currency?.symbol ?? companyForUser.currency?.code ?? ''}</span>
                              </td>
                              <td colSpan={isEditable ? 2 : 1}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </>
                  }
                </div>
              </div>

              <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
                <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
                  <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                      <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                    </svg>
                  </span>
                  <div className="d-grid">
                    <span className="text-start text-dark fw-bolder fs-5">{lang.labels.processOfUpdatingAnExpenseReportCompleted}</span>
                    <span className="text-justify text-muted fw-bold">
                      {lang.labels.clickOn}
                      <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.updateExpenseReport}</span>
                      {lang.labels.ifYouWishToSaveTheChangesMade}
                    </span>
                  </div>
                </div>
                <div className="d-flex gap-3">
                  { expenseReport.status === 'draft' &&
                    <button className="btn btn-lg btn-danger fw-bolder" type="button" onClick={submitExpenseReportDelete}>
                      <span className="text-uppercase text-nowrap">{lang.labels.delete}</span>
                    </button>
                  }
                  { (expenseReport.status === 'draft' || expenseReport.status === 'rejected') &&
                    <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitExpenseReportUpdate}>
                      <span className="indicator-label text-uppercase text-nowrap">{lang.labels.updateExpenseReport}</span>
                      <span className="indicator-progress">
                        {lang.labels.pleaseWait}
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
          <AppPurchaseViewDraft record={expenseReport as any} asideMinimize={asideMinimize} setAsideMinimize={setAsideMinimize}></AppPurchaseViewDraft>

          {/* Modals */}
          <ModalExpenseReportExpense
            companyForUser={companyForUser}
            editingExpense={editingExpense}
            defaultConcerning={concerning.value}
            prefillVoucherFile={prefillVoucherFile}
            prefillDocumentId={prefillDocumentId}
            prefillDocumentName={prefillDocumentName}
            onSave={handleModalSave}
            onCancel={handleModalCancel}
          />
          <AppModalAttachDocument
            id_company={companyForUser.id}
            entity_type="expense"
            onAttached={() => {}}
            onPick={handlePickDocument}
            modalId={MODAL_ATTACH_DOC_ID}
          />
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ExpenseReportUpdatePage;
